"use client";
import React, { useMemo, useRef, useState } from "react";
import { Feed } from "./Feed/Feed";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";

import useWindowSize from "@/hooks/useWindowSize";
import {
  getInstagramFeed,
  InstagramPost,
  type InstagramSettings,
} from "@/wp/getInstagram";
import { INITIAL_LOAD } from "./settings";
import { Modul_Instagram } from "@aleksundshantu/gp-types/acf-module";

export type InstagramModuleType = Modul_Instagram;

interface InstagramFeedProps {
  posts: InstagramPost[];
  postsTotal: number;
  followUrl?: string;
  settings: InstagramSettings;
  postSizes: string;
  modalAnimation: boolean;
}

const breakpoints = [
  { query: "(min-width: 1024px)", cols: 4 },
  { query: "(min-width: 768px)", cols: 3 },
  { query: "(min-width: 0px)", cols: 2 },
];

function getCurrentCols() {
  return breakpoints.find((bp) => window.matchMedia(bp.query).matches)?.cols;
}

export const InstagramFeed = ({
  posts,
  followUrl,
  postsTotal,
  settings,
  postSizes,
  modalAnimation,
}: InstagramFeedProps) => {
  const { getLabel } = useGlobalContext();

  const baseContainerRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [loadedPosts, setLoadedPosts] = useState<InstagramPost[]>(posts);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const { width } = useWindowSize();

  const currentCols = useMemo(() => {
    if (typeof window === "undefined" || !width) return 0;
    return getCurrentCols() ?? 0;
  }, [width]);

  const maxPages = settings.acf.max_pages ?? 3;

  const canLoadMore = currentPage < maxPages && loadedPosts.length < postsTotal;

  const visibleAmount = useMemo(() => {
    if (!canLoadMore) return loadedPosts.length;
    if (!currentCols) return 0;
    const fullRows = Math.floor(loadedPosts.length / currentCols);
    return fullRows * currentCols;
  }, [loadedPosts, currentCols, canLoadMore]);

  const initialFullRows = Math.floor(INITIAL_LOAD / currentCols);

  const loadMore = async () => {
    setLoading(true);
    setCurrentPage((p) => p + 1);

    const cols = getCurrentCols();
    if (!cols) return;
    const postsToLoad =
      loadedPosts.length % cols === 0
        ? cols
        : cols - (loadedPosts.length % cols);

    const newPosts = await getInstagramFeed({
      perPage: postsToLoad,
      page: 1,
      offset: loadedPosts.length,
    });
    setLoadedPosts((prev) => [...prev, ...newPosts.items]);
    setLoading(false);
  };

  return (
    <div className="feed-wrapper">
      <div ref={baseContainerRef}>
        {posts.length > 0 ? (
          <Feed
            props={{
              postSizes: postSizes,
              modalAnimation,
              images: loadedPosts.slice(0, visibleAmount),
              canLoadMore,
              cols: currentCols,
              loadMore,
              loading,
              loadMoreText: getLabel("load_more_label", "Mehr anzeigen"),
              followUrl,
              settings,
              followText: getLabel(
                "follow_on_instagram",
                "Folgen Sie mir auf Instagram",
              ),
              initialFullRows,
            }}
          />
        ) : (
          <p>
            {getLabel(
              "no_posts_instagram",
              "Zurzeit sind keine Instagram Posts verfügbar",
            )}
          </p>
        )}
      </div>
    </div>
  );
};
